import { WeatherConditions } from "common";
import { Image, YStack } from "tamagui";

import { GlowCircle } from "./GlowCircle";
import blizzardImage from "../assets/images/weather/blizzard.png";
import blowingSandImage from "../assets/images/weather/blowing-sand.png";
import cloudyImage from "../assets/images/weather/cloudy.png";
import dryImage from "../assets/images/weather/dry.png";
import foggyImage from "../assets/images/weather/foggy.png";
import fullMoonImage from "../assets/images/weather/full-moon.png";
import hailImage from "../assets/images/weather/hail.png";
import heavyRainImage from "../assets/images/weather/heavy-rain.png";
import heavyRainstormImage from "../assets/images/weather/heavy-rainstorm.png";
import highTemperatureImage from "../assets/images/weather/high-temperature.png";
import lightRainImage from "../assets/images/weather/light-rain.png";
import lightingImage from "../assets/images/weather/lighting.png";
import mistImage from "../assets/images/weather/mist.png";
import moderateSnowImage from "../assets/images/weather/moderate-snow.png";
import moonCloudyImage from "../assets/images/weather/moon-cloudy.png";
import partlyCloudyDaytimeImage from "../assets/images/weather/partly-cloudy-daytime.png";
import partlyCloudyNightImage from "../assets/images/weather/partly-cloudy-night.png";
import rainbowImage from "../assets/images/weather/rainbow.png";
import sandstormImage from "../assets/images/weather/sandstorm.png";
import snowyImage from "../assets/images/weather/snowy.png";
import sunCloudyImage from "../assets/images/weather/sun-cloudy.png";
import sunnyImage from "../assets/images/weather/sunny.png";
import thunderstormImage from "../assets/images/weather/thunderstorm.png";
import typhoonImage from "../assets/images/weather/typhoon.png";
import wetImage from "../assets/images/weather/wet.png";

export interface WeatherConditionsImageProps {
  conditions: WeatherConditions;
  size?: number;
  hasGlow?: boolean;
  glowSize?: number;
}

export const WeatherConditionsImage = ({
  conditions,
  size = 80,
  hasGlow = false,
  glowSize = size * 3,
}: WeatherConditionsImageProps) => {
  const uri = mapConditionToImageUri(conditions);
  const glowColor = mapConditionToGlowColorToken(conditions);

  return (
    <YStack
      width={size}
      height={size}
      position="relative"
      justifyContent="center"
      alignItems="center"
    >
      {hasGlow && (
        <GlowCircle
          position="absolute"
          top={-(glowSize / 2) + size / 2}
          left={-(glowSize / 2) + size / 2}
          size={glowSize}
          color={glowColor}
          opacity={0.15}
          gradientSize={`${glowSize / 2}px ${glowSize / 2}px`}
          gradientOrigin="50% 50%"
        />
      )}
      <Image
        source={{ uri, width: 128, height: 128 }}
        width={size}
        height={size}
      />
    </YStack>
  );
};

const mapConditionToGlowColorToken = (conditions: WeatherConditions) => {
  switch (conditions) {
    case WeatherConditions.Blizzard:
      return "$hgPrimary";
    case WeatherConditions.BlowingSand:
      return "$weatherOrangeOpaque";
    case WeatherConditions.Cloudy:
      return "$hgPrimary";
    case WeatherConditions.Dry:
      return "$weatherOrangeOpaque";
    case WeatherConditions.Foggy:
      return "$hgPrimary";
    case WeatherConditions.FullMoon:
      return "$hgPrimary";
    case WeatherConditions.Hail:
      return "$hgPrimary";
    case WeatherConditions.HeavyRain:
      return "$hgPrimary";
    case WeatherConditions.HeavyRainstorm:
      return "$hgPrimary";
    case WeatherConditions.HighTemperature:
      return "$glowRed";
    case WeatherConditions.LightRain:
      return "$hgPrimary";
    case WeatherConditions.Lighting:
      return "$hgPrimary";
    case WeatherConditions.Mist:
      return "$weatherOrangeOpaque";
    case WeatherConditions.ModerateSnow:
      return "$hgPrimary";
    case WeatherConditions.MoonCloudy:
      return "$hgPrimary";
    case WeatherConditions.PartlyCloudyDaytime:
      return "$weatherOrangeOpaque";
    case WeatherConditions.PartlyCloudyNight:
      return "$hgPrimary";
    case WeatherConditions.Rainbow:
      return "$weatherOrangeOpaque";
    case WeatherConditions.Sandstorm:
      return "$weatherOrangeOpaque";
    case WeatherConditions.Snowy:
      return "$hgPrimary";
    case WeatherConditions.SunCloudy:
      return "$weatherOrangeOpaque";
    case WeatherConditions.Sunny:
      return "$weatherOrangeOpaque";
    case WeatherConditions.Thunderstorm:
      return "$hgPrimary";
    case WeatherConditions.Typhoon:
      return "$hgPrimary";
    case WeatherConditions.Wet:
      return "$hgPrimary";
    default:
      return undefined;
  }
};

const mapConditionToImageUri = (conditions: WeatherConditions) => {
  switch (conditions) {
    case WeatherConditions.Blizzard:
      return blizzardImage;
    case WeatherConditions.BlowingSand:
      return blowingSandImage;
    case WeatherConditions.Cloudy:
      return cloudyImage;
    case WeatherConditions.Dry:
      return dryImage;
    case WeatherConditions.Foggy:
      return foggyImage;
    case WeatherConditions.FullMoon:
      return fullMoonImage;
    case WeatherConditions.Hail:
      return hailImage;
    case WeatherConditions.HeavyRain:
      return heavyRainImage;
    case WeatherConditions.HeavyRainstorm:
      return heavyRainstormImage;
    case WeatherConditions.HighTemperature:
      return highTemperatureImage;
    case WeatherConditions.LightRain:
      return lightRainImage;
    case WeatherConditions.Lighting:
      return lightingImage;
    case WeatherConditions.Mist:
      return mistImage;
    case WeatherConditions.ModerateSnow:
      return moderateSnowImage;
    case WeatherConditions.MoonCloudy:
      return moonCloudyImage;
    case WeatherConditions.PartlyCloudyDaytime:
      return partlyCloudyDaytimeImage;
    case WeatherConditions.PartlyCloudyNight:
      return partlyCloudyNightImage;
    case WeatherConditions.Rainbow:
      return rainbowImage;
    case WeatherConditions.Sandstorm:
      return sandstormImage;
    case WeatherConditions.Snowy:
      return snowyImage;
    case WeatherConditions.SunCloudy:
      return sunCloudyImage;
    case WeatherConditions.Sunny:
      return sunnyImage;
    case WeatherConditions.Thunderstorm:
      return thunderstormImage;
    case WeatherConditions.Typhoon:
      return typhoonImage;
    case WeatherConditions.Wet:
      return wetImage;
    default:
      return undefined;
  }
};
